import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, CaretUp, CaretDown } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";

/**
 * Admin panel: define the organization hierarchy levels (Employee → Team Lead
 * → Section Head → Department Manager → Director). Order matters — index 0
 * is the most junior level. Employees pick a level on their record and are
 * assigned a Line Manager; approvals route through the assigned manager.
 */
export default function HierarchyPanel() {
  const [levels, setLevels] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [newLevel, setNewLevel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/hierarchy/levels");
      setLevels(data.levels || []);
      setIsDefault(!!data.is_default);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const add = () => {
    const n = newLevel.trim();
    if (!n) return;
    if (levels.find((l) => l.toLowerCase() === n.toLowerCase())) {
      toast.error("Level already exists");
      return;
    }
    setLevels((L) => [...L, n]);
    setNewLevel("");
  };

  const remove = (i) => setLevels((L) => L.filter((_, idx) => idx !== i));

  const move = (i, delta) => {
    setLevels((L) => {
      const next = [...L];
      const j = i + delta;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const rename = (i, name) => setLevels((L) => L.map((v, idx) => idx === i ? name : v));

  const save = async () => {
    if (!levels.length) return toast.error("At least one level is required");
    setSaving(true);
    try {
      await api.put("/hierarchy/levels", { levels: levels.map((name) => ({ name })) });
      toast.success("Hierarchy saved — new assignments will use this order");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <section className="border border-zinc-950 mt-8" data-testid="hierarchy-panel">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">
        organization hierarchy
      </div>
      <div className="p-5">
        <p className="text-sm text-zinc-600 mb-4 max-w-2xl">
          Define the reporting ladder for your organization. Employee records pick a level and are assigned a Line Manager.
          Employee-submitted approvals (leave, expenses, etc.) route to the Line Manager assigned at the time the request is opened; if you reassign later, pending items follow the new manager automatically.
        </p>

        <div className="border border-zinc-200 mb-4" data-testid="hierarchy-levels-list">
          {levels.map((name, i) => (
            // Composite key: name (rarely duplicated in practice) + position keeps
            // React from misidentifying rows when the array is reordered.
            <div key={`${name || 'empty'}-${i}`} className="flex items-center gap-2 border-b border-zinc-100 px-3 py-2">
              <span className="font-mono text-[10px] text-zinc-400 w-6">L{i + 1}</span>
              <input
                value={name}
                onChange={(e) => rename(i, e.target.value)}
                data-testid={`hierarchy-level-${i}`}
                className="flex-1 border-b border-transparent focus:border-zinc-950 bg-transparent py-1 text-sm outline-none"
              />
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30" title="Move up" data-testid={`hierarchy-up-${i}`}>
                <CaretUp size={14} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === levels.length - 1} className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30" title="Move down" data-testid={`hierarchy-down-${i}`}>
                <CaretDown size={14} />
              </button>
              <button onClick={() => remove(i)} className="text-zinc-400 hover:text-[#D0021B]" title="Remove" data-testid={`hierarchy-remove-${i}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          {levels.length === 0 && (
            <div className="px-3 py-6 text-center text-xs font-mono text-zinc-400">NO LEVELS — ADD ONE BELOW</div>
          )}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <input
            value={newLevel}
            onChange={(e) => setNewLevel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Team Lead"
            data-testid="hierarchy-new-input"
            className="flex-1 max-w-sm border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none"
          />
          <button onClick={add} data-testid="hierarchy-add-btn" className="text-xs font-semibold border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white flex items-center gap-1">
            <Plus size={12} /> ADD LEVEL
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving || !levels.length}
            data-testid="hierarchy-save-btn"
            className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center gap-1"
          >
            <FloppyDisk size={12} /> {saving ? "SAVING…" : "SAVE HIERARCHY"}
          </button>
          {isDefault && <span className="text-[10px] font-mono text-zinc-500">USING BUILT-IN DEFAULTS</span>}
        </div>
      </div>
    </section>
  );
}
